import React, { useState } from 'react'
import Select from 'react-select'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { GET, POST, httpOk } from 'helpers/api'

/**
 * Перенос номенклатуры с логина на логин.
 *
 * Набор объектов задаётся галочками, а не двумя жёсткими кнопками, как было
 * раньше: клиенту чаще нужно «только элементы» или «блюда с техкартами», и
 * всё, что между этими сценариями, приходилось доделывать руками в базе.
 *
 * Техкарта тянет за собой сырьё и блюда: без них у состава не на что
 * ссылаться, поэтому соответствующие плитки включаются сами и блокируются.
 */
function TransferTab({ users }) {
	const { t } = useTranslation()

	const SCOPE_ITEMS = [
		{ 'key': 'menu', 'name': t('menu'), 'hint': t('admin_tools_menu_hint'), 'icon': 'uil-list-ul' },
		{ 'key': 'categories', 'name': t('categories'), 'hint': t('admin_tools_categories_hint'), 'icon': 'uil-apps' },
		{ 'key': 'elements', 'name': t('elements'), 'hint': t('admin_tools_elements_hint'), 'icon': 'uil-box' },
		{ 'key': 'preparations', 'name': t('preparations'), 'hint': t('admin_tools_preparations_hint'), 'icon': 'uil-layer-group' },
		{ 'key': 'products', 'name': t('products'), 'hint': t('admin_tools_products_hint'), 'icon': 'uil-restaurant' },
		{ 'key': 'recipes', 'name': t('admin_tools_recipes'), 'hint': t('admin_tools_recipes_hint'), 'icon': 'uil-sitemap' },
	]

	const [fromUser, setFromUser] = useState(null)
	const [toUser, setToUser] = useState(null)
	const [fromSummary, setFromSummary] = useState(null)
	const [toSummary, setToSummary] = useState(null)
	const [showModal, setShowModal] = useState(false)
	const [result, setResult] = useState(null)
	const [scope, setScope] = useState({
		'menu': false,
		'categories': false,
		'elements': true,
		'preparations': false,
		'products': false,
		'recipes': false,
	})

	// Состав нельзя перенести без того, к чему он крепится, — бэк всё равно
	// достроит эти галочки, поэтому показываем это сразу на форме.
	const forcedByRecipes = ['elements', 'preparations', 'products']

	function isChecked(key) {
		if (scope.recipes && forcedByRecipes.includes(key)) return true
		return !!scope[key]
	}

	function toggleScope(key) {
		if (scope.recipes && forcedByRecipes.includes(key)) return
		setScope({ ...scope, [key]: !scope[key] })
	}

	async function getSummary(login, setSummary) {
		setSummary(null)
		if (!login) return

		const response = await GET('/admin/duplicate/summary', { 'login': login }, { loader: false })
		if (httpOk(response)) setSummary(response.data)
	}

	function handleSelectFrom(option) {
		setFromUser(option)
		setResult(null)
		getSummary(option?.login, setFromSummary)
	}

	function handleSelectTo(option) {
		setToUser(option)
		setResult(null)
		getSummary(option?.login, setToSummary)
	}

	function openConfirm() {
		if (!fromUser || !toUser) {
			toast.error(t('admin_tools_select_logins'))
			return
		}

		if (fromUser.login === toUser.login) {
			toast.error(t('admin_tools_same_login'))
			return
		}

		if (!SCOPE_ITEMS.some((item) => isChecked(item.key))) {
			toast.error(t('admin_tools_select_scope'))
			return
		}

		setShowModal(true)
	}

	async function transfer() {
		setShowModal(false)
		setResult(null)

		const payload = {
			'from_login': fromUser.login,
			'to_login': toUser.login,
		}
		for (const item of SCOPE_ITEMS) {
			payload[item.key] = isChecked(item.key)
		}

		const response = await POST('/admin/duplicate', payload, { loader: true, notification: false })

		if (httpOk(response)) {
			setResult(response.data)
			toast.success(response.data?.message || t('success'))
			getSummary(toUser.login, setToSummary)
		}
	}

	function summaryValue(summary, key) {
		if (!summary) return '—'
		return summary[key] ?? 0
	}

	return (
		<>
			<div className="row">
				<div className="col-md-8">
					<div className="card mb-3">
						<div className="card-body">
							<div className="tool-section-title">
								{t('admin_tools_direction')}
							</div>
							<div className="text-muted fz-12 mb-3">
								{t('admin_tools_transfer_hint')}
							</div>

							<div className="row align-items-end">
								<div className="form-group col-md-5">
									<label>{t('admin_tools_from_login')}</label>
									<Select
										options={users}
										value={fromUser}
										onChange={(option) => handleSelectFrom(option)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => `${option.login} — ${option.name || ''}`}
										getOptionValue={(option) => option.id}
									/>
								</div>
								<div className="col-md-2 text-center d-none d-md-block">
									<i className="uil uil-arrow-right fz-24 text-muted"
										style={{ 'lineHeight': '38px' }}></i>
								</div>
								<div className="form-group col-md-5">
									<label>{t('admin_tools_to_login')}</label>
									<Select
										options={users}
										value={toUser}
										onChange={(option) => handleSelectTo(option)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => `${option.login} — ${option.name || ''}`}
										getOptionValue={(option) => option.id}
									/>
								</div>
							</div>
						</div>
					</div>

					<div className="card">
						<div className="card-body">
							<div className="tool-section-title">
								{t('admin_tools_what_to_transfer')}
							</div>
							<div className="text-muted fz-12 mb-3">
								{t('admin_tools_scope_hint')}
							</div>

							<div className="row g-2">
								{SCOPE_ITEMS.map((item) => (
									<div className="col-md-6" key={item.key}>
										<div className={`tool-tile
											${isChecked(item.key) ? 'active' : ''}
											${(scope.recipes && forcedByRecipes.includes(item.key)) ? 'disabled' : ''}`}
											onClick={() => toggleScope(item.key)}>
											<input type="checkbox" className="form-check-input"
												checked={isChecked(item.key)}
												onChange={() => { }} />
											<div className="w-100">
												<div className="d-flex justify-content-between">
													<div className="tool-tile-title">
														<i className={`uil ${item.icon} me-1`}></i>
														{item.name}
													</div>
													<div className="fz-12 text-muted text-nowrap">
														{summaryValue(fromSummary, item.key)}
													</div>
												</div>
												<div className="tool-tile-hint">
													{item.hint}
												</div>
											</div>
										</div>
									</div>
								))}
							</div>

							<button type="button" className="btn btn-primary w-100 mt-3"
								onClick={() => openConfirm()}>
								{t('admin_tools_transfer_start')}
							</button>
						</div>
					</div>
				</div>

				<div className="col-md-4">
					<div className="card mb-3">
						<div className="card-body">
							<div className="tool-section-title">
								{t('admin_tools_comparison')}
							</div>
							{/* Логины выведены строкой над таблицей: в колонку они не
							    помещаются и рвутся по буквам. */}
							<div className="text-muted fz-12 mb-2 text-truncate">
								{fromUser ? fromUser.login : '—'}
								<i className="uil uil-arrow-right mx-1"></i>
								{toUser ? toUser.login : '—'}
							</div>
							<div className="tool-result">
								<div className="tool-result-row fz-12 text-muted">
									<span>{t('admin_tools_object')}</span>
									<span className="d-flex gap-3">
										<span style={{ 'width': '50px', 'textAlign': 'right' }}>
											{t('admin_tools_from_login')}
										</span>
										<span style={{ 'width': '50px', 'textAlign': 'right' }}>
											{t('admin_tools_to_login')}
										</span>
									</span>
								</div>
								{SCOPE_ITEMS.map((item) => (
									<div className="tool-result-row" key={item.key}>
										<span>{item.name}</span>
										<span className="d-flex gap-3">
											<span style={{ 'width': '50px', 'textAlign': 'right' }}>
												{summaryValue(fromSummary, item.key)}
											</span>
											<span style={{ 'width': '50px', 'textAlign': 'right' }}>
												{summaryValue(toSummary, item.key)}
											</span>
										</span>
									</div>
								))}
							</div>
						</div>
					</div>

					{result &&
						<div className="card">
							<div className="card-body">
								<div className="tool-section-title">
									{t('admin_tools_transfer_result')}
								</div>
								<div className="tool-result">
									<div className="tool-result-row fz-12 text-muted">
										<span>{t('admin_tools_object')}</span>
										<span className="d-flex gap-3">
											<span style={{ 'width': '50px', 'textAlign': 'right' }}>
												{t('created')}
											</span>
											<span style={{ 'width': '50px', 'textAlign': 'right' }}>
												{t('admin_tools_reused')}
											</span>
										</span>
									</div>
									{SCOPE_ITEMS.map((item) => (
										<div className="tool-result-row" key={item.key}>
											<span>{item.name}</span>
											<span className="d-flex gap-3">
												<span className="text-success"
													style={{ 'width': '50px', 'textAlign': 'right' }}>
													{result.created?.[item.key] ?? 0}
												</span>
												<span className="text-muted"
													style={{ 'width': '50px', 'textAlign': 'right' }}>
													{result.reused?.[item.key] ?? 0}
												</span>
											</span>
										</div>
									))}
								</div>
								{result.skipped?.recipes > 0 &&
									<div className="text-danger fz-12 mt-2">
										Не перенесено строк техкарт: {result.skipped.recipes} — сырьё удалено у источника и живой позиции с таким названием нет
									</div>
								}
							</div>
						</div>
					}
				</div>
			</div>

			{/* MODAL */}
			<Modal show={showModal} animation={false} centered
				onHide={() => setShowModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('confirm')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="mb-2">
						{t('admin_tools_confirm_transfer')}
					</div>
					<div className="mb-2">
						<b>{fromUser?.login}</b>
						<i className="uil uil-arrow-right mx-2"></i>
						<b>{toUser?.login}</b>
					</div>
					<div className="fz-14">
						{SCOPE_ITEMS.filter((item) => isChecked(item.key)).map((item) => (
							<span className="badge bg-primary me-1" key={item.key}>
								{item.name}
							</span>
						))}
					</div>
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex w-100">
						<button type="button" className="btn btn-warning w-100 me-2"
							onClick={() => setShowModal(false)}>
							{t('cancel')}
						</button>
						<button type="button" className="btn btn-primary w-100"
							onClick={() => transfer()}>
							{t('confirm')}
						</button>
					</div>
				</Modal.Footer>
			</Modal>
		</>
	)
}

export default TransferTab
